import React, { useState, useEffect } from 'react';
import client from '../api/client';

export default function MedicationStep({ data, update, checkInType }) {
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get('/medications')
      .then(res => {
        setMedications(res.data);
        // Initialize logs if empty — only include meds relevant to this check-in type
        if (!data.medicationLogs?.length && res.data.length > 0) {
          const relevant = res.data.filter(med => {
            if (checkInType === 'MORNING') return med.schedule !== 'EVENING';
            if (checkInType === 'EVENING') return med.schedule !== 'MORNING';
            return true;
          });
          update({
            medicationLogs: relevant.map(med => ({
              medicationId: med.id,
              taken: false,
            })),
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggleMed = (medicationId) => {
    const logs = data.medicationLogs || [];
    const existing = logs.find(l => l.medicationId === medicationId);
    if (existing) {
      update({
        medicationLogs: logs.map(l =>
          l.medicationId === medicationId ? { ...l, taken: !l.taken } : l
        ),
      });
    } else {
      update({
        medicationLogs: [...logs, { medicationId, taken: true }],
      });
    }
  };

  if (loading) {
    return <p className="text-white/50">Loading medications...</p>;
  }

  const EGG_FREEZE_NAMES = ['Women\'s Daily Multivitamin', 'CoQ10 + Folate', 'Daily Iron Ease', 'Omega DHA + EPA'];
  const morningMeds = medications.filter(m => m.schedule === 'MORNING' && !EGG_FREEZE_NAMES.includes(m.name));
  const eggFreezeMeds = medications.filter(m => EGG_FREEZE_NAMES.includes(m.name));
  const eveningMeds = medications.filter(m => m.schedule === 'EVENING');
  const otherMeds = medications.filter(m => !m.schedule);

  const showMorning = checkInType !== 'EVENING';
  const showEvening = checkInType !== 'MORNING';

  const renderMedButton = (med) => {
    const log = (data.medicationLogs || []).find(l => l.medicationId === med.id);
    const isTaken = log?.taken || false;

    return (
      <button
        key={med.id}
        type="button"
        onClick={() => toggleMed(med.id)}
        className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition
          ${isTaken
            ? 'border-white/30 bg-white/15'
            : 'border-white/10 bg-white/5 hover:border-white/20'
          }`}
      >
        <div className="text-left">
          <p className="font-medium text-white">{med.name}</p>
          {med.dosage && (
            <p className="text-sm text-white/50">{med.dosage}</p>
          )}
        </div>
        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center
          ${isTaken ? 'border-white bg-white text-black' : 'border-white/30'}`}>
          {isTaken && <span className="text-xs">✓</span>}
        </div>
      </button>
    );
  };

  const renderToggle = (label, subtitle, value, onChange) => (
    <div className="flex items-center justify-between p-4 rounded-xl border-2 border-white/10 bg-white/5">
      <div>
        <p className="font-medium text-white">{label}</p>
        {subtitle && <p className="text-sm text-white/50">{subtitle}</p>}
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onChange(false)}
          className={`px-3 py-1 rounded-lg text-sm font-medium transition
            ${value === false
              ? 'bg-white/20 text-white ring-2 ring-white/30'
              : 'bg-white/10 text-white/50 hover:bg-white/20'
            }`}
        >
          No
        </button>
        <button
          type="button"
          onClick={() => onChange(true)}
          className={`px-3 py-1 rounded-lg text-sm font-medium transition
            ${value === true
              ? 'bg-white/20 text-white ring-2 ring-white/30'
              : 'bg-white/10 text-white/50 hover:bg-white/20'
            }`}
        >
          Yes
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Morning Meds */}
      {showMorning && morningMeds.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-semibold text-white/60 uppercase tracking-wide">Morning Meds</label>
            <button
              type="button"
              onClick={() => {
                const logs = data.medicationLogs || [];
                const allMorningTaken = morningMeds.every(med => {
                  const log = logs.find(l => l.medicationId === med.id);
                  return log?.taken;
                });
                const updatedLogs = logs.map(l => {
                  const isMorning = morningMeds.some(m => m.id === l.medicationId);
                  return isMorning ? { ...l, taken: !allMorningTaken } : l;
                });
                update({ medicationLogs: updatedLogs });
              }}
              className={`text-xs px-3 py-1 rounded-lg font-medium transition
                ${morningMeds.every(med => (data.medicationLogs || []).find(l => l.medicationId === med.id)?.taken)
                  ? 'bg-white/20 text-white hover:bg-white/25'
                  : 'bg-white/10 text-white/60 hover:bg-white/15'
                }`}
            >
              {morningMeds.every(med => (data.medicationLogs || []).find(l => l.medicationId === med.id)?.taken)
                ? 'Deselect All'
                : 'Select All'}
            </button>
          </div>
          {morningMeds.map(renderMedButton)}
        </div>
      )}

      {/* Egg Freeze Support Supplements */}
      {showMorning && eggFreezeMeds.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-semibold text-white/60 uppercase tracking-wide">Egg Freeze Support</label>
            <button
              type="button"
              onClick={() => {
                const logs = data.medicationLogs || [];
                const allTaken = eggFreezeMeds.every(med => {
                  const log = logs.find(l => l.medicationId === med.id);
                  return log?.taken;
                });
                const updatedLogs = logs.map(l => {
                  const isEggFreeze = eggFreezeMeds.some(m => m.id === l.medicationId);
                  return isEggFreeze ? { ...l, taken: !allTaken } : l;
                });
                update({ medicationLogs: updatedLogs });
              }}
              className={`text-xs px-3 py-1 rounded-lg font-medium transition
                ${eggFreezeMeds.every(med => (data.medicationLogs || []).find(l => l.medicationId === med.id)?.taken)
                  ? 'bg-white/20 text-white hover:bg-white/25'
                  : 'bg-white/10 text-white/60 hover:bg-white/15'
                }`}
            >
              {eggFreezeMeds.every(med => (data.medicationLogs || []).find(l => l.medicationId === med.id)?.taken)
                ? 'Deselect All'
                : 'Select All'}
            </button>
          </div>
          {eggFreezeMeds.map(renderMedButton)}
        </div>
      )}

      {/* Evening Meds */}
      {showEvening && eveningMeds.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-semibold text-white/60 uppercase tracking-wide">Evening Meds</label>
          {eveningMeds.map(renderMedButton)}
        </div>
      )}

      {/* Other Meds (no schedule) */}
      {otherMeds.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-semibold text-white/60 uppercase tracking-wide">Other</label>
          {otherMeds.map(renderMedButton)}
        </div>
      )}

      {medications.length === 0 && (
        <div className="text-center py-4 text-white/50">
          <p>No medications or supplements set up yet.</p>
        </div>
      )}

      {/* As-needed medications */}
      <div className="border-t border-white/10 pt-4 space-y-3">
        <label className="text-sm font-semibold text-white/60 uppercase tracking-wide">As Needed</label>
        {renderToggle(
          'Ibuprofen',
          null,
          data.tookIbuprofen ?? false,
          (v) => update({ tookIbuprofen: v })
        )}
        {renderToggle(
          'Muscle Relaxant',
          'Tizanidine',
          data.tookMuscleRelaxant ?? false,
          (v) => update({ tookMuscleRelaxant: v })
        )}
      </div>
    </div>
  );
}
